import {
  Button,
  Combobox,
  Dialog,
  Field,
  Flex,
  IconButton,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
  Textarea,
  useFilter,
  useListCollection,
} from '@chakra-ui/react'
import PhoneInput from '@/components/PhoneInput'
import { useState, useEffect } from 'react'
import { LuPlus, LuTrash2 } from 'react-icons/lu'
import type { CreateEventReminderRequest } from '@/services/events'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateEvent } from '@/hooks/events/useCreateEvent'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import type { ApiErrorResponse } from '@/lib/api'
import type { Agenda } from '@/services/agendas'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agendas?: Agenda[]
  defaultDate?: string
  onSuccess?: () => void
}

const CreateEventDialog = ({ open, onOpenChange, agendas, defaultDate, onSuccess }: Props) => {
  const { agendaId: paramAgendaId } = useParams<{ agendaId: string }>()

  const queryClient = useQueryClient()
  const [selectedAgendaId, setSelectedAgendaId] = useState('')
  const agendaId = agendas ? selectedAgendaId : paramAgendaId!

  const { startsWith } = useFilter({ sensitivity: 'base' })

  const { collection, filter, reset, set } = useListCollection<Agenda>({
    initialItems: agendas ?? [],
    itemToValue: (item) => item.id,
    itemToString: (item) => item.name,
    filter: startsWith,
  })

  useEffect(() => {
    if (agendas) set(agendas)
  }, [agendas, set])

  const createMutation = useCreateEvent(agendaId!)
  const [reminders, setReminders] = useState<CreateEventReminderRequest[]>([])

  const validationErrors = (createMutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data?.validation_errors

  const handleClose = (open: boolean) => {
    if (!open) {
      createMutation.reset()
      setReminders([])
    }
    onOpenChange(open)
  }

  const addReminder = () => setReminders((prev) => [...prev, { reminder_date: '', note: null }])

  const removeReminder = (index: number) => setReminders((prev) => prev.filter((_, i) => i !== index))

  const updateReminder = (index: number, field: keyof CreateEventReminderRequest, value: string) =>
    setReminders((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: field === 'note' ? value || null : value } : r))
    )

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    const name = (data.get('name') as string).trim()
    const notes = (data.get('notes') as string).trim() || null
    const event_date = data.get('event_date') as string
    const event_time = (data.get('event_time') as string).trim() || null
    const status = Number(data.get('status'))
    const contractor_name = (data.get('contractor_name') as string).trim() || null
    const contractor_phone = (data.get('contractor_phone') as string).trim() || null

    const payload = { name, notes, event_date, event_time, status, contractor_name, contractor_phone, reminders: reminders.length > 0 ? reminders : null }

    createMutation.mutate(payload, {
      onSuccess: () => {
        showSuccess('Evento criado com sucesso!')
        if (agendas) queryClient.invalidateQueries({ queryKey: ['calendar'] })
        form.reset()
        handleClose(false)
        onSuccess?.()
      },
    })
  }

  return (
    <Dialog.Root
      size="md"
      placement="center"
      open={open}
      onOpenChange={({ open }) => handleClose(open)}
      initialFocusEl={() => null}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content
            m={4}
            maxH="calc(100dvh - 2rem)"
            display="flex"
            flexDirection="column"
          >
            <form
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
            >
              <Dialog.Header flexShrink={0}>
                <Dialog.Title>Novo evento</Dialog.Title>
              </Dialog.Header>

              <Dialog.Body overflowY="auto" flex="1">
                <Stack gap="4">
                  {agendas && (
                    <Field.Root required>
                      <Field.Label>
                        Agenda
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Combobox.Root
                        collection={collection}
                        value={selectedAgendaId ? [selectedAgendaId] : []}
                        onInputValueChange={(e) => filter(e.inputValue)}
                        onValueChange={({ items }) => setSelectedAgendaId(items[0]?.id ?? '')}
                        openOnClick
                        variant="subtle"
                      >
                        <Combobox.Control>
                          <Combobox.Input placeholder="Buscar agenda..." />
                          <Combobox.IndicatorGroup>
                            <Combobox.ClearTrigger />
                            <Combobox.Trigger onClick={reset} />
                          </Combobox.IndicatorGroup>
                        </Combobox.Control>
                        <Combobox.Positioner>
                          <Combobox.Content zIndex="popover">
                            <Combobox.Empty>Nenhuma agenda encontrada</Combobox.Empty>
                            {collection.items.map((item) => (
                              <Combobox.Item item={item} key={item.id}>
                                {item.name}
                                <Combobox.ItemIndicator />
                              </Combobox.Item>
                            ))}
                          </Combobox.Content>
                        </Combobox.Positioner>
                      </Combobox.Root>
                    </Field.Root>
                  )}

                  <Field.Root required invalid={!!validationErrors?.name}>
                    <Field.Label>
                      Nome
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      name="name"
                      placeholder="Ex: Reunião de planejamento"
                      variant="subtle"
                      maxLength={150}
                    />
                    {validationErrors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Stack direction={{ base: 'column', sm: 'row' }} gap="4">
                    <Field.Root required invalid={!!validationErrors?.event_date} flex="1">
                      <Field.Label>
                        Data
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        name="event_date"
                        type="date"
                        variant="subtle"
                        defaultValue={defaultDate ?? ''}
                      />
                      {validationErrors?.event_date?.map((error, index) => (
                        <Field.ErrorText key={index}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root invalid={!!validationErrors?.event_time} flex="1">
                      <Field.Label>Horário</Field.Label>
                      <Input
                        name="event_time"
                        type="time"
                        variant="subtle"
                      />
                      {validationErrors?.event_time?.map((error, index) => (
                        <Field.ErrorText key={index}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>
                  </Stack>

                  <Field.Root required invalid={!!validationErrors?.status}>
                    <Field.Label>
                      Status
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <NativeSelect.Root variant="subtle">
                      <NativeSelect.Field name="status" defaultValue={0}>
                        <option value={0}>Reservado</option>
                        <option value={1}>Confirmado</option>
                        <option value={2}>Cancelado</option>
                        <option value={3}>Procurado</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {validationErrors?.status?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Stack direction={{ base: 'column', sm: 'row' }} gap="4">
                    <Field.Root invalid={!!validationErrors?.contractor_name} flex="1">
                      <Field.Label>Nome do contratante</Field.Label>
                      <Input
                        name="contractor_name"
                        placeholder="Ex: João Silva"
                        variant="subtle"
                        maxLength={150}
                      />
                      {validationErrors?.contractor_name?.map((error, index) => (
                        <Field.ErrorText key={index}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root invalid={!!validationErrors?.contractor_phone} flex="1">
                      <Field.Label>Telefone do contratante</Field.Label>
                      <PhoneInput
                        name="contractor_phone"
                        variant="subtle"
                      />
                      {validationErrors?.contractor_phone?.map((error, index) => (
                        <Field.ErrorText key={index}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>
                  </Stack>

                  <Field.Root invalid={!!validationErrors?.notes}>
                    <Field.Label>Notas</Field.Label>
                    <Textarea
                      name="notes"
                      placeholder="Observações sobre o evento (opcional)"
                      rows={4}
                      variant="subtle"
                      maxLength={500}
                    />
                    {validationErrors?.notes?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Stack gap="2">
                    <Flex justify="space-between" align="center">
                      <Text fontWeight="medium" fontSize="sm">Lembretes</Text>
                      <Button size="xs" variant="ghost" onClick={addReminder} color="primary.500" fontWeight={600}>
                        <LuPlus />
                        Adicionar
                      </Button>
                    </Flex>
                    {reminders.length === 0 && (
                      <Text fontSize="xs" color="fg.muted">Nenhum lembrete adicionado a este evento</Text>
                    )}
                    {reminders.map((reminder, index) => (
                      <Stack key={index} gap="2" p="3" borderWidth="1px" borderRadius="md">
                        <Flex gap="2" align="flex-end">
                          <Field.Root required flex="1">
                            <Field.Label fontSize="xs">Data</Field.Label>
                            <Input
                              type="date"
                              size="sm"
                              variant="subtle"
                              value={reminder.reminder_date}
                              onChange={(e) => updateReminder(index, 'reminder_date', e.target.value)}
                            />
                          </Field.Root>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            colorPalette="red"
                            aria-label="Remover lembrete"
                            onClick={() => removeReminder(index)}
                          >
                            <LuTrash2 />
                          </IconButton>
                        </Flex>
                        <Field.Root>
                          <Field.Label fontSize="xs">Nota (opcional)</Field.Label>
                          <Input
                            size="sm"
                            variant="subtle"
                            placeholder="Ex: Confirmar presença"
                            maxLength={200}
                            value={reminder.note ?? ''}
                            onChange={(e) => updateReminder(index, 'note', e.target.value)}
                          />
                        </Field.Root>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Dialog.Body>

              <Dialog.Footer flexShrink={0}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={createMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button size="sm" type="submit" colorPalette="primary" loading={createMutation.isPending}>
                  Criar evento
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default CreateEventDialog
